import { Button, ButtonProps } from '@amzn/awsui-components-core';
import { ICellRendererParams } from 'ag-grid-community';
import React, { useCallback, useMemo, useState } from 'react';
import { getCompanyCode } from 'src/constants/company-planning-mapping';
import { CorpSegmentNames } from 'src/constants/corp-segment-constants';
import { CorpSegmentDefaultValues } from './column-generator/generators/InitialColumnGenerator';

/**
 * Order of corp segments for concatenation: company-location-cost center-account-product-channel-project
 */
const CORP_SEGMENT_ORDER_FOR_COPY = [
  CorpSegmentNames.COMPANY,
  CorpSegmentNames.LOCATION,
  CorpSegmentNames.COST_CENTER,
  CorpSegmentNames.ACCOUNT,
  CorpSegmentNames.PRODUCT_LINE,
  CorpSegmentNames.CHANNEL,
  CorpSegmentNames.PROJECT
];

/**
 * Generates the concatenated corp segment value from row data
 * @param rowData - The AG Grid row data
 * @param corpSegmentDefaultValues - Default values for non-mandatory corp segments
 * @param useDefaults - Whether to use default values for empty fields
 * @returns Concatenated string in format: company-location-cost_center-account-product_line-channel-project
 */
const getConcatenatedCorpSegmentValue = (
  rowData: any,
  corpSegmentDefaultValues: CorpSegmentDefaultValues = {},
  useDefaults: boolean = false
): string => {
  if (!rowData) return '';

  const values = CORP_SEGMENT_ORDER_FOR_COPY.map((segmentName) => {
    let value = rowData[segmentName];

    // If value is empty/null and we have a default value for this segment, use the default (only if useDefaults is true)
    if (!value && useDefaults && corpSegmentDefaultValues[segmentName]) {
      value = corpSegmentDefaultValues[segmentName];
    }

    // For Company segment, convert Planning Company code to Company Code using the mapping
    // (The data stores Planning Company like US_P, but we need Company Code like 2D)
    if (segmentName === CorpSegmentNames.COMPANY && value) {
      return getCompanyCode(value);
    }

    return value ? String(value) : '';
  });

  // Join all values with hyphen (including empty ones to maintain position)
  // This ensures the pattern shows all segments: company-location-cost_center-account-product_line-channel-project
  return values.join('-');
};

/**
 * Data required for PO list modal display
 */
export interface POListModalData {
  lineItemId: string;
  poList: string[];
}

/**
 * Context interface for the Forecast Grid that can be passed via AG Grid's context prop
 */
export interface ForecastGridContext {
  /** Callback when line item ID is clicked to show PO list */
  onLineItemClick?: (data: POListModalData) => void;
  /** Callback to create a PO for a line item */
  onCreatePO?: (lineItemId: string) => void;
  /** Callback to show POs for a line item */
  onShowPOs?: (lineItemId: string) => void;
}

interface LineItemIdCellRendererProps extends ICellRendererParams {
  /** Default values for non-mandatory corp segments */
  corpSegmentDefaultValues?: CorpSegmentDefaultValues;
  /** Callback when line item ID is clicked to show PO list (can also be passed via context) */
  onLineItemClick?: (data: POListModalData) => void;
}

/**
 * Custom cell renderer for Line Item Id column with copy functionality and PO list modal trigger.
 * Displays the Line Item Id value with a copy button that copies concatenated corp segment values.
 * If the line item has tagged POs, the line item ID is displayed as a clickable link.
 */
const LineItemIdCellRenderer: React.FC<LineItemIdCellRendererProps> = (params) => {
  const [copyStatus, setCopyStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Handle footer row
  if (params.node.footer) {
    return <span style={{ fontWeight: 'bold' }}>Total</span>;
  }

  const lineItemId = params.value;
  const rowData = params.data;

  // Get PO list from row data
  const poList: string[] = rowData?.po_list || [];
  const hasPOs = poList.length > 0;

  // Get default values from cell renderer params
  const corpSegmentDefaultValues = params.corpSegmentDefaultValues || {};

  // Get callback for line item click - can be passed via cellRendererParams or via AG Grid context
  const onLineItemClick = params.onLineItemClick || (params.context as ForecastGridContext)?.onLineItemClick;

  // Actual value from row data (shown on hover)
  const actualValue = useMemo(() => getConcatenatedCorpSegmentValue(rowData, corpSegmentDefaultValues, false), [rowData, corpSegmentDefaultValues]);

  // Value with defaults filled in (used for copying)
  const valueWithDefaults = useMemo(
    () => getConcatenatedCorpSegmentValue(rowData, corpSegmentDefaultValues, true),
    [rowData, corpSegmentDefaultValues]
  );

  const handleCopyToClipboard: ButtonProps['onClick'] = async (event) => {
    // Prevent the click from affecting row selection
    event.stopPropagation();

    if (!valueWithDefaults) return;

    try {
      await navigator.clipboard.writeText(valueWithDefaults);
      setCopyStatus('success');
      setTimeout(() => setCopyStatus('idle'), 2000);
    } catch (err) {
      setCopyStatus('error');
      setTimeout(() => setCopyStatus('idle'), 2000);
    }
  };

  const handleLineItemClick = useCallback(
    (event: React.MouseEvent | CustomEvent) => {
      // Prevent the click from affecting row selection and default navigation
      event.stopPropagation();
      event.preventDefault();

      if (onLineItemClick && lineItemId) {
        onLineItemClick({
          lineItemId: String(lineItemId),
          poList
        });
      }
    },
    [onLineItemClick, lineItemId, poList]
  );

  // Render the line item ID - show as a clickable link only if there are valid POs
  const renderLineItemId = () => {
    if (lineItemId && onLineItemClick && hasPOs) {
      const ariaLabel = `View ${poList.length} PO(s) tagged to line item ${lineItemId}`;
      return (
        <span
          role="button"
          tabIndex={0}
          onClick={handleLineItemClick as any}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleLineItemClick(e as any);
            }
          }}
          style={{
            color: '#0972d3',
            textDecoration: 'underline',
            cursor: 'pointer'
          }}
          aria-label={ariaLabel}
        >
          {lineItemId}
        </span>
      );
    }
    return <span>{lineItemId}</span>;
  };

  // If no value, just show the line item id without copy button
  if (!actualValue && !valueWithDefaults) {
    return renderLineItemId();
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', width: '100%' }}>
      <span style={{ flexGrow: 1 }}>{renderLineItemId()}</span>
      <Button iconName="copy" variant="inline-icon" onClick={handleCopyToClipboard} ariaLabel={`Copy GL String: ${valueWithDefaults}`} />
      {copyStatus === 'success' && <span style={{ color: 'green', fontSize: '10px', marginLeft: '2px' }}>✓</span>}
    </div>
  );
};

export default LineItemIdCellRenderer;
